import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

import { useCollapse } from "./CollapseContext";
import { Icon } from "../Icon";
import LpgCollapseOutline from "../../icons/Lpg/CollapseOutline";
import LpgExpandOutline from "../../icons/Lpg/ExpandOutline";
import styles from "./ComparisonTable.scss";

type ComparisonTableShowMoreBaseProps = {
  showMoreText?: string;
  showLessText?: string;
};

export type ComparisonTableShowMoreProps = ComparisonTableShowMoreBaseProps &
  Omit<
    React.ComponentPropsWithoutRef<"div">,
    "children" | keyof ComparisonTableShowMoreBaseProps
  >;

const defaultProps = {
  showMoreText: "Mehr anzeigen",
  showLessText: "Weniger anzeigen",
} as const;

const validatedDefaultProps: ReactDefaultProps<
  ComparisonTableShowMoreBaseProps,
  typeof defaultProps
> = defaultProps;

export const ComparisonTableShowMore: ((
  props: ComparisonTableShowMoreProps
) => JSX.Element | null) &
  ReactComponentStaticProps<ComparisonTableShowMoreProps> = ({
  showMoreText = defaultProps.showMoreText,
  showLessText = defaultProps.showLessText,
  ...restProps
}) => {
  const [node, setNode] = useState<Element | null>(null);
  const { collapseRows, setCollapseRows } = useCollapse();

  useEffect(() => {
    setNode(document.querySelector("#show-more-portal-target"));
  }, []);

  if (!node) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={styles["show-more"]} {...restProps}>
      <button
        className={styles["show-more-button"]}
        type="button"
        onClick={() => setCollapseRows((prev) => !prev)}
        aria-expanded={!collapseRows}
      >
        {collapseRows ? showMoreText : showLessText}{" "}
        <span className={styles["show-more-button-icon"]}>
          <Icon
            icon={collapseRows ? LpgExpandOutline : LpgCollapseOutline}
            size={Icon.Size.parent}
          />
        </span>
      </button>
    </div>,
    node
  );
};

ComparisonTableShowMore.propTypes = {
  showMoreText: PropTypes.string,
  showLessText: PropTypes.string,
};

ComparisonTableShowMore.defaultProps = validatedDefaultProps;

ComparisonTableShowMore.displayName = "ComparisonTable.ShowMore";

export default ComparisonTableShowMore;
